import { Injectable, Injector } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GUID_EMPTY, TransactionType } from '@core';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { CurrencyPricePipe } from '@delon/util';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { ArKnockoffService } from 'src/app/shared-feature/services/knockoff/ar-knockoff.service';
import { AttachmentChangePayload } from 'src/app/shared/models/attachment-change.model';
import { ValidPostingPeriodCheckingService } from 'src/app/shared/services/valid-posting-period-checking.service';

@Injectable()
export class ReceivePaymentService extends DocBaseService {
  customerdata: any;
  private componentRef: any;

  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'aror';
    this.parentMenuAclKey = 'customer';
    this.transactionType = TransactionType.AROR;

    this.instantDataService.request$.subscribe(request => {
      if (request.context === 'aror') {
        this.replaceInstantData(request); // Call initInstantData with the current data
      }
    });
  }

  setComponentRef(ref: any) {
    this.componentRef = ref;
  }

  get knockoffSrv() {
    return this.injector.get(ArKnockoffService);
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  get isEInvReqField() {
    return this.isEInvEnabled && this.isEInvReqSystemData;
  }

  get vppSrv() {
    return this.injector.get(ValidPostingPeriodCheckingService);
  }

  public get isGovBirCat(): boolean {
    const cust = this.form.controls.customer.value;
    if (cust) {
      return cust.isGovBirCategory;
    }
    return false;
  }

  public get toKnockoff(): any {
    const toKnockoff =
      this.injector.get(ActivatedRoute).snapshot.queryParams['toKnockoff'];
    return toKnockoff;
  }

  public get toKnockoffBatch(): any {
    const toKnockoff =
      this.injector.get(ActivatedRoute).snapshot.queryParams['toKnockoffBatch'];
    return toKnockoff;
  }

  form = this.createForm();

  updatingRate = false;
  einvPaymOptions: any[] = [];

  clone() {
    let obj = { ...this._data };
    delete obj.id;
    obj.docCode = '';
    obj.docDate = new Date();
    obj.postDate = new Date();
    obj.taxDate = new Date();
    if (obj.isPast) {
      const newdate = new Date(
        this.cutoffDate.getFullYear(),
        this.cutoffDate.getMonth(),
        0
      );
      obj.docDate = newdate;
      obj.postDate = newdate;
      obj.taxDate = newdate;
    }
    obj.isCancelled = false;
    obj.cancelledDate = null;
    obj.cancelledRemark = null;
    obj.isBouncedCheque = false;
    obj.bouncedChequeDate = null;
    obj.depositFrom = null;
    obj.depositFromDocCode = null;
    obj.depositFromReferenceId = null;
    obj.details = [];
    obj.knockoff = [];
    obj.refundAmount = 0;

    if (this.parkAndPost) obj.documentStatus = 0;

    return obj;
  }

  new() {
    const customerId =
      this.injector.get(ActivatedRoute).snapshot.queryParams['customerId'];
    const docType =
      this.injector.get(ActivatedRoute).snapshot.queryParams['docType'];
    const toKnockoff =
      this.injector.get(ActivatedRoute).snapshot.queryParams['toKnockoffBatch'];

    if (toKnockoff) {
      return this.http.post('api/arreceipts/TransferDocsToAROR?_silent=true', {
        _silent: true,
        customerId: customerId,
        docIds: toKnockoff,
        docType: docType
      }).pipe(
      map(response => {
        // Fix postDate if it's null because
        // 1- it is not nullable in backend
        // 2- moreDatePicker modal does not show null date
        if (response && response.postDate === null) {
          response.postDate = response.docDate || new Date();
        }
        return response;
      })
    );
    }
    return this.http.get('api/arreceipts/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/arreceipts/${id}`);
  }

  getDepositRecords(depositGroupId: any | null): Observable<any> {
    return this.http.get(
      `api/arreceipts/depositrecords?depositGroupId=${depositGroupId}`
    );
  }

  create(body: any) {
    return this.http.post('api/arreceipts/create/', body);
  }

   update(body: any) {
    return this.http.post('api/arreceipts/update/', body).pipe(
      catchError(error => {
        if (error.error?.code === 'NEEDS_CONFIRMATION') {
          return new Observable(subscriber => {
            this.modalService.confirm({
              nzTitle: error.error.message.includes('Bank Recon')
                ? 'Bank Reconciliation Confirmation'
                : 'Knock-off Confirmation',
              nzContent: `${error.error.message}<br><br>Are you sure you want to save?`,
              nzOkText: 'Proceed',
              nzOkType: 'primary',
              nzOnOk: () => {
                const queryParam = error.error.message.includes('Bank Recon')
                  ? 'confirmedForBankRecon=true'
                  : 'confirmedForKnockOff=true';
                this.http
                  .post(`api/arreceipts/update/?${queryParam}`, body)
                  .subscribe({
                    next: res => subscriber.next(res),
                    error: err => subscriber.error(err)
                  });
              },
              nzCancelText: 'Cancel',
              nzOnCancel: () =>
                subscriber.error(new Error('Update canceled by user.'))
            });
          });
        }
        return throwError(() => error);
      })
    );
  }

  override onAction(action: string, _: any): Promise<boolean> {
    const type = action?.toLowerCase();
    return new Promise((resolve, reject) => {
      switch (type) {
        case 'new':
          this.knockoffSrv!.knockoffItems = [];
          resolve(true);
          break;
        case 'edit':
          this.knockoffSrv!.reload(
            this._data.customerId,
            'OR',
            this._data.id,
            null,
            []
          );
          resolve(true);
          break;
        case 'save':
          this.handleSave()
            .then(() => resolve(true))
            .catch(() => reject(false));
          break;
        case 'void':
        case 'setbounced':
          this.confirmAction(type)
            .then(() => resolve(true))
            .catch(() => reject(false));
          break;
        default:
          resolve(true);
          break;
      }
    });
  }

  confirmAmount(amount: number) {
    const amountStr = this.injector.get(CurrencyPricePipe).transform(amount);
    return new Promise((resolve, reject) => {
      this.modalService.confirm({
        nzTitle: `Confirmation`,
        nzContent: `Total amount is less than matched amount. <br /> Do you want to set total amount to ${amountStr}?`,
        nzOkText: 'Yes',
        nzOkType: 'primary',
        nzOnOk: () => {
          resolve(true);
        },
        nzCancelText: 'No',
        nzOnCancel: () => {
          reject();
        }
      });
    });
  }

  confirmAction(action: string) {
    return new Promise((resolve, reject) => {
      const title =
        action === 'delete'
          ? 'Delete Record?'
          : action === 'void'
            ? 'Cancel Record?'
            : 'Mark Record as Bounced Cheque?';
      const modalRef = this.modalService.confirm({
        nzTitle: title,
        nzContent: '<b>Matched data will be cleared.</b>',
        nzOkText: 'Proceed',
        nzOkType: 'primary',
        nzOnOk: () => {
          resolve(true);
          modalRef.destroy();
        },
        nzCancelText: 'Cancel',
        nzOnCancel: () => {
          reject();
          modalRef.destroy();
        }
      });
    });
  }

  void(body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post('api/arreceipts/void', body).pipe(
        catchError(error => {
          if (error.error?.code === 'NEEDS_CONFIRMATION') {
            return new Observable(subscriber => {
              this.modalService.confirm({
                nzTitle: error.error.message.includes('Bank Recon')
                  ? 'Bank Reconciliation Confirmation'
                  : 'Knock-off Confirmation',
                nzContent: `${error.error.message}<br><br>Are you sure you want to proceed?`,
                nzOkText: 'Proceed',
                nzOkType: 'primary',
                nzOnOk: () => {
                  const queryParam = error.error.message.includes('Bank Recon')
                    ? 'confirmedForBankRecon=true'
                    : 'confirmedForKnockOff=true';
                  this.http
                    .post(`api/arreceipts/void?${queryParam}`, body)
                    .subscribe({
                      next: res => subscriber.next(res),
                      error: err => subscriber.error(err)
                    });
                },
                nzCancelText: 'Cancel',
                nzOnCancel: () =>
                  subscriber.error(new Error('Cancel canceled by user.'))
              });
            });
          }
          return throwError(() => error);
        })
      ).subscribe({
        next: res => {
          resolve(res);
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  devoid(id: string) {
    return this.http.post(`api/arreceipts/devoid`, { id });
  }

  setBounced(body: any) {
    return this.http.post('api/arreceipts/setbounced', body);
  }

  clearChq(id: string) {
    return this.http.post('api/arreceipts/clearcheque', { id });
  }

  setPDC() {
    this._data.isPostDatedCheque = !this._data.isPostDatedCheque;
  }

  setAndSavePDC(id: string) {
    return this.http.post('api/arreceipts/MarkPdc', { id });
  }

  setAndSaveUnmarkPDC(id: string) {
    return this.http.post('api/arreceipts/UnmarkPdc', { id });
  }

  transferToSo() {}

  transferToDo() {}

  transferToSinv() {}

  transferToCs() {}

  transferToCsr() {}

  transferToPo() {}

  transferToGrn() {}

  transferToPinv() {}

  transferToCp() {}

  transferToCpf() {}

  doDocTransfer() {
    return EMPTY;
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.confirmAction('delete')
        .then(() => {
          this.http
            .delete(`api/arreceipts`, null, { body: { ids: [id] } })
            .subscribe({
              next: res => resolve(res),
              error: () => reject()
            });
        })
        .catch(() => reject());
    });
  }

  initForm(data: any) {
    // if (this.isEInvReqField) {
    //   this.form.controls.eInvoicePaymentMode.setValue('01');
    //   this.form.controls.eInvoicePaymentMode.setValidators([Validators.required]);
    //   this.form.controls.eInvoicePaymentMode.updateValueAndValidity({ onlySelf: true});
    // }

    this.buildLines(
      this.form.controls.multiPayments,
      () => this.createMultiPaymentForm(),
      data.multiPayments?.length ?? 0
    );

    if (data && data.isMultiPayment) {
      this.form.controls.accountId.clearValidators();
      this.form.controls.accountId.updateValueAndValidity({ onlySelf: true });
    }
    
    // Initialize BIR 2307 Collected fields
    if (data) {
      this.form.controls.isBIR2307Collected.setValue(data.isBir2307Collected || false);
      this.form.controls.biR2307CollectedDate.setValue(data.bir2307CollectedDate ? new Date(data.bir2307CollectedDate) : null);
    }
  }

  private handleSave(): Promise<boolean> {
    if (this.instantData && this.instantData.enabled) {
      // only triggers if the amount is different
      if (
        this.form &&
        this.form.value &&
        this.instantData.data.amount !== this.form.value.totalAmount
      ) {
        return new Promise((resolve, reject) => {
          const modalRef = this.modalService.confirm({
            nzTitle: 'Transaction amount differs from bank recon amount.',
            nzContent:
              'This transaction will not be auto-matched. Are you sure you want to proceed?',
            nzOkText: 'Proceed',
            nzOkType: 'primary',
            nzOnOk: () => {
              this.validateMatchAmount()
                .then(() => resolve(true))
                .catch(() => reject(false));
            },
            nzCancelText: 'Cancel',
            nzOnCancel: () => {
              modalRef.destroy();
              reject(false);
            }
          });
        });
      }
    }

    return this.validateMatchAmount();
  }

  private validateMatchAmount(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const matchedAmount = this.knockoffSrv.matchedAmount;
      const totalAmt = +(<number>this.form.controls.totalAmount.value || 0);
      const refund = +(<number>this.form.controls.refundAmount.value || 0);
      const maxMatchable = +Math.max(0, totalAmt - refund).toFixed(2);
      if (matchedAmount > maxMatchable) {
        const newTotal = +(matchedAmount + refund).toFixed(2);
        this.confirmAmount(newTotal)
          .then(() => {
            this.form.controls.totalAmount.setValue(newTotal);
            this.vppSrv
              .check(this.form.value.docDate)
              .then(() => resolve(true))
              .catch(() => reject(false));
          })
          .catch(() => reject(false));
      } else {
        this.vppSrv
          .check(this.form.value.docDate)
          .then(() => resolve(true))
          .catch(() => reject(false));
      }
    });
  }

  override initParam(data: any) {
    return this.initInstantData(data);
  }

  private initInstantData(data: any) {
    const request = this.instantDataService.getRequest('aror');

    if (request && request.data) {
      data.account = request.data.bankAccount;
      data.accountId = request.data.bankAccountId;
      data.description = request.data.description;
      data.docDate = request.data.statementDate;
      data.postDate = request.data.statementDate;
      data.referenceNo = request.data.refNo;
      data.totalAmount = request.data.amount;

      this.instantData = { ...request, enabled: true };
      this.instantDataService.removeRequest('aror');
    }

    return data;
  }

  private replaceInstantData(request: any) {
    const controls = this.form.controls;
    controls.account.setValue(request.data.bankAccount);
    controls.accountId.setValue(request.data.bankAccountId);
    controls.description.setValue(request.data.description);
    controls.totalAmount.setValue(request.data.amount);
    controls.referenceNo.setValue(request.data.refNo);
    controls.docDate.setValue(request.data.statementDate);
    controls.postDate.setValue(request.data.statementDate);

    this.instantData = { ...request, enabled: true };
  }

  override notifyInstantData(data: any) {
    if (this.instantData.enabled) {
      const notify =
        this.instantData.data.amount === this.form.value.totalAmount;
      if (notify) {
        this.instantDataService.addData(
          this.instantData.key,
          this.instantData.id,
          data
        );
      }

      this.instantData = {};
    }
  }

  createForm() {
    const form = this.fb.group({
      docCode: ['', [Validators.required, Validators.maxLength(50)]],
      docDate: [new Date(), [Validators.required]],
      postDate: [new Date(), [Validators.required]],
      taxDate: [new Date(), [Validators.required]],
      customerId: ['', [Validators.required]],
      customer: [<any>null, [Validators.required]],
      accountId: [<any>null, [Validators.required]],
      account: [<any>null, []],
      bankChargesAccountId: ['', []],
      bankChargesAccount: [<any>null, []],
      currencyId: ['', [Validators.required]],
      currency: [<any>null, [Validators.required]],
      currencyRate: [1, [Validators.required]],
      agentId: ['', []],
      agent: [<any>null, []],
      referenceNo: ['', [Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(250)]],
      notes: ['', [Validators.maxLength(500)]],
      remark1: ['', [Validators.maxLength(100)]],
      remark2: ['', [Validators.maxLength(100)]],
      remark3: ['', [Validators.maxLength(100)]],
      remark4: ['', [Validators.maxLength(100)]],
      remark5: ['', [Validators.maxLength(100)]],
      projectId: [<any>null, []],
      project: [<any>null, []],
      totalAmount: [0, []],
      refundAmount: [0, []],
      bankChargesAmount: [0, []],
      bankChargesAmountLocal: [0, []],
      isPast: [false, []],
      knockoff: [<any>null, []],
      isBIR2307Collected: [false, []],
      biR2307CollectedDate: [<any>null, []],
      isMultiPayment: [false, []],
      multiPayments: this.fb.array([this.createMultiPaymentForm()]),
      //Einvoice field
      attention: ['', [Validators.maxLength(200)]],
      customerName: ['', [Validators.maxLength(250)]],
      customerPhone: ['', [Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(300)]],
      customerTinType: [<any>null, []],
      tinNumber: ['', [Validators.maxLength(14)]],
      registrationNo: ['', [Validators.maxLength(40)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      // eInvoicePaymentMode: ['01', []],
    });

    form.controls.agent.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.agentId.setValue(val?.id);
    });

    form.controls.postDate.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      // if post date is earlier than doc date, set doc date to post date
      val = new Date(val!);
      const isEarlier =
        new Date(val!.toDateString()) <
        new Date(this.form.controls.postDate.value!.toDateString());

      if (isEarlier === true) {
        this.form.controls.docDate.setValue(val);
      }
    });

    form.controls.docDate.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.taxDate.setValue(val);

      this.observeChanges = false;
      form.controls.postDate.setValue(val);
      this.observeChanges = true;

      val = new Date(val!);
      const isEarlier =
        new Date(val!.toDateString()) <
        new Date(this.cutoffDate.toDateString());
      if (isEarlier === true) {
        this.form.controls.isPast.setValue(true);
        this.form.controls.accountId.clearValidators();
        this.form.controls.accountId.updateValueAndValidity({ onlySelf: true });
      } else {
        this.form.controls.isPast.setValue(false);
        const isMultiPayment = this.form.controls.isMultiPayment.value;
        if (isMultiPayment === true) {
          this.form.controls.accountId.clearValidators();
        } else {
          this.form.controls.accountId.setValidators([Validators.required]);
        }
        this.form.controls.accountId.updateValueAndValidity({ onlySelf: true });
      }
    });

    form.controls.account.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val && val.id !== GUID_EMPTY) {
        form.controls.accountId.setValue(val.id);

        const params: any = { id: val.id };

        // Only add customerId if it has a value
        if (
          form.controls.customerId.value !== null &&
          form.controls.customerId.value !== undefined
        ) {
          params.customerId = form.controls.customerId.value;
        }

        this.http.get('api/accountcodes/details', params).subscribe({
          next: res => {
            val.bal = res.balance;
            if (this.shouldSkipCurrencySyncFromBank(res.currency)) {
              return;
            }
            form.controls.currency.setValue(res.currency);
          }
        });
      }
    });

    form.controls.customer.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val && val.id > 0) {
        form.controls.customerId.setValue(val.id);
        form.controls.customerName.setValue(val.name);

        this.http
          .get(`api/customers/${val.id}/defaultvalues`)
          .subscribe(res => {
            if (res) {
              form.patchValue({
                agent: res.agent,
                agentId: res.agent?.id,
                currency: res.currency,
                currencyId: res.currency.id,
                currencyRate: res.currency.defaultSalesRate,
                attention: res.contactPerson,
                customerName: res.name,
                customerPhone: res.phoneNo1,
                email: res.email,
                customerTinType: res.tinType,
                tinNumber: res.tinNumber,
                registrationNo: res.registrationNo,
                salesTaxRegNo: res.salesTaxRegNo,
                serviceTaxRegNo: res.serviceTaxRegNo,
                address1: res.address1,
                address2: res.address2,
                address3: res.address3,
                address4: res.address4,
                zipCode: res.zipCode,
                city: res.city,
                state: res.state,
                country: res.country
              });
            }
          });
      } else {
        form.controls.customerId.setValue(null);
        form.controls.customerName.setValue(null);
      }

      // Clear knockoff items when customer changes
      form.controls.knockoff.setValue([]);
    });

    form.controls.currency.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);

      this.observeChanges = false;
      if (val.local) {
        this.form.controls.currencyRate.setValue(1);
      } else {
        this.form.controls.currencyRate.setValue(val?.defaultSalesRate);
      }
      for (let line of this.form.value.knockoff) {
        this.recalcLine(line);
      }
      this.recalcHeader(form);
      this.observeChanges = true;
    });

    form.controls.currencyRate.valueChanges.subscribe(_ => {
      if (!this.observeChanges) return;

      this.observeChanges = false;
      this.recalcHeader(form);
      this.observeChanges = true;
    });

    form.controls.project.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.projectId.setValue(val?.id);
    });

    form.controls.isMultiPayment.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val) {
        form.controls.accountId.setValue(null);
        form.controls.account.setValue(null);
        form.controls.accountId.clearValidators();
        form.controls.accountId.updateValueAndValidity({ onlySelf: true });

        form.controls.bankChargesAccountId.setValue(null);
        form.controls.bankChargesAccount.setValue(null);
        form.controls.bankChargesAmountLocal.setValue(0);

        form.controls.referenceNo.setValue(null);
      }
      else{
        form.controls.accountId.setValidators([Validators.required]);
        form.controls.accountId.updateValueAndValidity({ onlySelf: true });
        
        form.controls.multiPayments.clear();
      }
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    this.userDefaultService.setControlRequired(form.get('agent')!, 'Agent');
    this.userDefaultService.setControlRequired(form.get('project')!, 'Project');

    return form;
  }

  /** When editing, keep transaction currency/rate if the new bank uses the same currency. */
  private shouldSkipCurrencySyncFromBank(bankCurrency: any): boolean {
    const docId = this._data?.id;
    if (docId == null || Number(docId) === 0) return false;
    const txnCurrency = this.form.controls.currency.value;
    if (!txnCurrency || !bankCurrency) return false;
    if (txnCurrency.id != null && bankCurrency.id != null) {
      return txnCurrency.id === bankCurrency.id;
    }
    return txnCurrency.code === bankCurrency.code;
  }

  updateLatestRate() {
    this.updatingRate = true;
    this.http
      .post('api/currencies/latestrate?_silent=true', {
        currencyCode: this.form.controls.currency.value.code,
        isAR: true
      })
      .subscribe({
        next: res => {
          if (res) {
            this.form.controls.currencyRate.setValue(res);
          }
          this.updatingRate = false;
        },
        error: () => (this.updatingRate = false)
      });
  }

  updateCustInfo(data: any) {
    this.form.controls.customerName.setValue(data.customerName);
    this.form.controls.tinNumber.setValue(data.tinNumber);
    this.form.controls.registrationNo.setValue(data.registrationNo);
    this.form.controls.customerPhone.setValue(data.customerPhone);
    this.form.controls.email.setValue(data.email);
    this.form.controls.salesTaxRegNo.setValue(data.salesTaxRegNo);
    this.form.controls.serviceTaxRegNo.setValue(data.serviceTaxRegNo);
    this.form.controls.customerTinType.setValue(data.customerTinType);
    this.form.controls.zipCode.setValue(data.zipCode);
    this.form.controls.city.setValue(data.city);
    this.form.controls.state.setValue(data.state);
    this.form.controls.country.setValue(data.country);
    this.form.controls.address1.setValue(data.address1);
    this.form.controls.address2.setValue(data.address2);
    this.form.controls.address3.setValue(data.address3);
    this.form.controls.address4.setValue(data.address4);
  }

  recalcLine(line: FormGroup) {}

  recalcHeader(header: FormGroup) {}

  override getAttachmentChangesPayload(): AttachmentChangePayload | null {
    if (this.componentRef && this.componentRef.collectAttachmentChanges) {
      return this.componentRef.collectAttachmentChanges();
    }
    return null;
  }

  override clearAttachmentChanges(): void {
    if (this.componentRef && this.componentRef.clearAttachmentChanges) {
      this.componentRef.clearAttachmentChanges();
    }
  }
}
